import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { AppShell } from "@/components/AppShell";
import { Tag, Truck, CheckCircle2, Clock, XCircle, DollarSign } from "lucide-react";
import { listMySellerOffers, type MySellerOffer } from "@/lib/seller-offers.functions";
import { PriceInline } from "@/lib/currency";

export const Route = createFileRoute("/_authenticated/my-listings")({
  head: () => ({ meta: [{ title: "My Sale Offers — Want To Buy" }] }),
  component: MyListingsPage,
});

type Group = "active" | "pending" | "past";

function classify(o: MySellerOffer): Group {
  const txnStatus = o.transaction?.status ?? null;
  if (txnStatus && ["funds_held", "shipped", "delivered", "disputed"].includes(txnStatus)) return "active";
  if (txnStatus && ["released", "refunded", "cancelled"].includes(txnStatus)) return "past";
  if (o.status === "pending") return "pending";
  if (o.status === "accepted") return "active";
  return "past"; // declined / expired / withdrawn
}

function statusLabel(o: MySellerOffer): { label: string; tone: string; Icon: typeof Clock } {
  const t = o.transaction?.status;
  if (t === "shipped") return { label: "Shipped — awaiting delivery", tone: "text-blue-300 bg-blue-500/10", Icon: Truck };
  if (t === "delivered") return { label: "Delivered — payout pending", tone: "text-success bg-success/10", Icon: CheckCircle2 };
  if (t === "funds_held") return { label: "Payment received — ship now", tone: "text-primary bg-primary/10", Icon: Truck };
  if (t === "released") return { label: "Funds released", tone: "text-success bg-success/10", Icon: DollarSign };
  if (t === "refunded") return { label: "Refunded to buyer", tone: "text-muted-foreground bg-muted", Icon: XCircle };
  if (t === "disputed") return { label: "Disputed", tone: "text-destructive bg-destructive/10", Icon: XCircle };
  if (o.status === "accepted") return { label: "Accepted — awaiting buyer payment", tone: "text-primary bg-primary/10", Icon: Clock };
  if (o.status === "pending") return { label: "Pending buyer response", tone: "text-muted-foreground bg-muted", Icon: Clock };
  if (o.status === "declined") return { label: "Declined", tone: "text-muted-foreground bg-muted", Icon: XCircle };
  if (o.status === "expired") return { label: "Expired", tone: "text-muted-foreground bg-muted", Icon: XCircle };
  return { label: o.status, tone: "text-muted-foreground bg-muted", Icon: Clock };
}

function money(cents: number, currency: string) {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

function MyListingsPage() {
  const fetchOffers = useServerFn(listMySellerOffers);
  const { data, isLoading } = useQuery({
    queryKey: ["seller-offers", "mine"],
    queryFn: () => fetchOffers(),
  });

  const offers = data?.offers ?? [];
  const groups: Record<Group, MySellerOffer[]> = { active: [], pending: [], past: [] };
  for (const o of offers) groups[classify(o)].push(o);

  return (
    <AppShell>
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold flex items-center gap-2">
          <Tag className="h-7 w-7 text-primary" /> My Sale Offers
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Offers you've submitted to fulfil open buy orders. Click any offer to view the buyer's shipping details once payment is received.
        </p>
      </div>

      {isLoading && <div className="panel p-10 text-center text-sm text-muted-foreground">Loading…</div>}

      {!isLoading && offers.length === 0 && (
        <div className="panel p-10 text-center text-sm text-muted-foreground">
          <p>You haven't submitted any offers yet.</p>
          <p className="mt-2">
            <Link to="/" className="text-primary hover:underline">Browse open buy orders</Link> to submit your first offer.
          </p>
        </div>
      )}

      {!isLoading && offers.length > 0 && (
        <div className="space-y-8">
          <Section title="Active" items={groups.active} emptyHint="No active sales right now." />
          <Section title="Pending" items={groups.pending} emptyHint="No pending offers." />
          <Section title="Past" items={groups.past} emptyHint="No past offers." />
        </div>
      )}
    </AppShell>
  );
}

function Section({ title, items, emptyHint }: { title: string; items: MySellerOffer[]; emptyHint: string }) {
  return (
    <section>
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">{title}</h2>
      {items.length === 0 ? (
        <div className="panel p-6 text-center text-xs text-muted-foreground">{emptyHint}</div>
      ) : (
        <div className="panel divide-y divide-border">
          {items.map((o) => <OfferRow key={o.id} offer={o} />)}
        </div>
      )}
    </section>
  );
}

function OfferRow({ offer }: { offer: MySellerOffer }) {
  const bo = offer.buy_order;
  const { label, tone, Icon } = statusLabel(offer);
  const buyOrderId = bo?.id;
  const heading = bo?.title || bo?.card_name || "Buy order";

  const body = (
    <div className="flex items-start gap-3 p-4 transition hover:bg-muted/40">
      <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-md ${tone}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="truncate font-semibold text-sm">{heading}</div>
            {bo?.tcg && <div className="text-xs text-muted-foreground">{bo.tcg}</div>}
          </div>
          <div className="shrink-0 text-right">
            <div className="font-mono text-sm"><PriceInline usd={offer.price_cents / 100} /></div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">your offer</div>
          </div>
        </div>
        <div className={`mt-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] ${tone}`}>{label}</div>
      </div>
    </div>
  );

  if (!buyOrderId) {
    return <div className="opacity-60">{body}</div>;
  }
  return (
    <Link to="/orders/$id" params={{ id: buyOrderId }} className="block">
      {body}
    </Link>
  );
}
